import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [status, setStatus] = useState({ payment_status: "pending" });
    const [tries, setTries] = useState(0);
    const { clear } = useCart();
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                setStatus(r.data);
                if (r.data.payment_status === "paid") {
                    if (!cleared.current) { clear(); cleared.current = true; }
                    return;
                }
                if (["failed", "expired", "refunded"].includes(r.data.payment_status)) return;
            } catch (e) {}
            if (!cancelled && tries < 10) {
                setTimeout(() => setTries((t) => t + 1), 2000);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, tries, clear]);

    const paid = status.payment_status === "paid";
    const failed = ["failed", "expired"].includes(status.payment_status);

    return (
        <div className="relative z-10 pt-40 pb-32 min-h-screen" data-testid="payment-success-page">
            <div className="max-w-2xl mx-auto px-6 text-center">
                {paid ? (
                    <>
                        <CheckCircle2 className="w-16 h-16 text-[#8C3A3A] mx-auto mb-8" />
                        <div className="label-caps text-[#8C3A3A] mb-4">Payment Confirmed</div>
                        <h1 className="font-serif-display text-5xl text-[#1A1A1A] font-light tracking-tighter mb-6">Thank you.</h1>
                        <p className="text-[#5A5A5A] leading-relaxed mb-10">
                            Your order has been received. A confirmation is on its way to your inbox and your print will
                            ship within 3–5 business days.
                        </p>
                        <Link to="/gallery" className="inline-block bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] hover:text-white transition-colors" data-testid="success-back-btn">
                            Continue browsing
                        </Link>
                    </>
                ) : failed ? (
                    <>
                        <XCircle className="w-16 h-16 text-[#8C3A3A] mx-auto mb-8" />
                        <h1 className="font-serif-display text-5xl text-[#1A1A1A] font-light mb-6">Payment failed</h1>
                        <Link to="/cart" className="inline-block border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide">Return to cart</Link>
                    </>
                ) : (
                    <>
                        <div className="w-12 h-12 border-2 border-[#8C3A3A] border-t-transparent rounded-full animate-spin mx-auto mb-8" />
                        <h1 className="font-serif-display text-4xl text-[#1A1A1A] font-light mb-4">Confirming payment...</h1>
                        <p className="text-[#5A5A5A]">One moment while we verify with Stripe.</p>
                    </>
                )}
            </div>
        </div>
    );
}

export function PaymentCancel() {
    return (
        <div className="relative z-10 pt-40 pb-32 min-h-screen text-center px-6" data-testid="payment-cancel-page">
            <div className="max-w-2xl mx-auto">
                <div className="label-caps text-[#8C3A3A] mb-4">Cancelled</div>
                <h1 className="font-serif-display text-5xl text-[#1A1A1A] font-light tracking-tighter mb-6">Checkout cancelled</h1>
                <p className="text-[#5A5A5A] mb-10">No charge was made — your cart is still saved.</p>
                <Link to="/cart" className="inline-block border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors">Return to cart</Link>
            </div>
        </div>
    );
}
